import "./bootstrap";
import "../css/app.css";
import "photoswipe/style.css";
import "driver.js/dist/driver.css";
import "vue3-toastify/dist/index.css";

import * as Sentry from "@sentry/vue";
import Clarity from "@microsoft/clarity";
import { createSSRApp, h, defineAsyncComponent, nextTick } from "vue";
import { createInertiaApp, router } from "@inertiajs/vue3";
import { getInitialPageFromDOM } from "@inertiajs/core";
import { ZiggyVue } from "../../vendor/tightenco/ziggy";
import { ensureVueformReady, preloadVueform, setVueformLocale } from './runtime/lazyVueform';

import { createPinia } from "pinia";
import {
    registryTarget,
    resolveIcon,
    resolveLayoutImporter,
    resolvePageImporter,
    resolveFrontendMessages,
    resolveServiceWorkerUrl,
    resolvePublicStaticCache,
} from "@tenant-registry";
import VLottie from "./Components/VLottie.vue";
import { createFrontendI18n, syncFrontendLocale } from "./i18n/createFrontendI18n";
import { getTenantContext, isFeatureEnabled, setTenantIdentity, setTenantLayout } from "./composables/useTenant";
import { forgetStoredTutorialSessions, useTutorialManager } from "./composables/useTutorialManager";
import { syncTileAuthFromPage } from "./state/tileAuth";
import axios from 'axios';
import { installPublicSessionGuard } from './runtime/publicSession';
import { selectionStore } from './stores/selectionStore';
import { preparePublicServiceWorker } from './runtime/publicServiceWorker';
import { createClientPublicPageLayoutResolver } from './runtime/publicPageLayout';
import {
    iconPackRequests,
    readInitialIcons,
    resolveInitialIcons,
    resolveInitialLayouts,
    waitForInitialNavigation,
} from './runtime/initialHydration';
import iconPacks from 'virtual:catalog-icon-packs';
import { preloadLottiePlayer } from './runtime/lottiePlayer';
import { installMapVersionRefresh } from './modules/map-assets/versionRefresh';
import { startBrowserServices, syncTutorialSession } from './runtime/browserServices';

const appName = import.meta.env.VITE_APP_NAME || "Laravel";
const tenantContext = getTenantContext();
const tenantTheme = tenantContext.theme || "Default";
const shouldRegisterServiceWorker = import.meta.env.PROD || import.meta.env.VITE_ENABLE_SW === "true";

const pinia = createPinia();
const initialPage = getInitialPageFromDOM('app');
const disposeMapVersionRefresh = installMapVersionRefresh();
if (import.meta.hot) import.meta.hot.dispose(disposeMapVersionRefresh);
const usesPublicCachePolicy = ['Pompei', 'Omnia'].includes(tenantTheme) || initialPage?.props?.publicAccess?.enabled === true;
installPublicSessionGuard({
    router,
    initialPage,
    http: axios,
    reset: () => {
        selectionStore.clear();
        selectionStore.setPageItems([]);
        for (const store of pinia._s.values()) {
            if (typeof store.resetForIdentityChange === 'function') store.resetForIdentityChange();
            else if (store.$id === 'notifications') store.clear();
        }
        syncTileAuthFromPage({ props: {} });
        setTenantIdentity();
    },
});
setTenantIdentity(initialPage?.props?.auth?.user ?? {}, initialPage?.props?.auth?.is_super_admin === true);
syncTileAuthFromPage(initialPage);
router.on('navigate', (event) => syncTileAuthFromPage(event.detail.page));
const initialLocalization = initialPage?.props?.localization ?? {};
const frontendMessagesPromise = resolveFrontendMessages(
    tenantTheme,
    initialLocalization.interface_locales ?? [initialLocalization.locale ?? 'it'],
);
// Fetched alongside the messages and the page: hydration must not wait for them.
// The theme pack covers the icons rendered later on the client as well.
const initialIconsPromise = resolveInitialIcons(
    [...readInitialIcons(document), ...iconPackRequests(tenantTheme, iconPacks)],
    resolveIcon,
);

// Per-user tutorial state: initialize the session as soon as the app loads
// with an authenticated user injected into the client context server-side.
// For guests this is a no-op and tutorial checks simply return false.
// Tenants whose profile disables tutorials neither write nor keep these dates.
syncTutorialSession({
    profile: tenantContext.browserServices,
    userId: tenantContext.user?.id,
    tutorialManager: useTutorialManager(),
    forgetStoredTutorialSessions,
});

const layoutImporter = resolveLayoutImporter(getTenantContext().theme);
if (!layoutImporter) {
    throw new Error(`Authenticated layout not found for theme: ${tenantTheme}`);
}
setTenantLayout(defineAsyncComponent(layoutImporter));

let clientApp;
let initialPageRequiresVueform = false;
const resolvePublicPageLayout = createClientPublicPageLayoutResolver();
const pageThemeOf = (page) => page?.props?.tenant_theme || page?.props?.tenant?.theme || tenantTheme;

// Start the initial page code together with the messages rather than after
// them; Inertia's resolver below reuses the same module request.
if (initialPage?.component) {
    resolvePageImporter(initialPage.component, pageThemeOf(initialPage))?.()?.catch(() => {});
}

async function resolveTenantPageComponent(name, requestPage) {
    const pageTheme = pageThemeOf(requestPage);
    console.log(`Resolving page component for: ${name} in theme: ${pageTheme}`);
    const importer = resolvePageImporter(name, pageTheme);
    if (importer) {
        const page = await importer();
        if (page.default?.requiresVueform) {
            if (clientApp) {
                await ensureVueformReady(clientApp);
            } else {
                initialPageRequiresVueform = true;
                await preloadVueform();
            }
        }
        const resolved = resolvePublicPageLayout(page, name, pageTheme, () => defineAsyncComponent(resolveLayoutImporter(pageTheme)));
        if (!clientApp && resolved.default?.layout) {
            // Resolve the asynchronous layout components themselves (with their CSS,
            // including the tenant layout of workspace callbacks): an unresolved one
            // would discard and rebuild the server-rendered layout after mount().
            await resolveInitialLayouts(resolved.default.layout, getTenantContext().layout);
        }
        return resolved;
    }

    throw new Error(`Page component not found: ${name}`);
}

const normalizeTenantSlug = (value) => {
    return (value || "default")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, "-");
};

const registerTenantServiceWorker = () => {
    if (!shouldRegisterServiceWorker || !("serviceWorker" in navigator)) {
        return;
    }

    const tenantName = getTenantContext().name || tenantTheme || appName || "default";
    const tenantSlug = normalizeTenantSlug(tenantName);

    const tenantsWithDedicatedServiceWorker = new Set([
        'carracci',
        'carracci-stg',
        'kroton',
        'simbolo-bologna',
        'horo',
        'hera',
        'hera-stg',
        'pompei',
    ]);

    const resolvedTenant = tenantTheme === 'Pompei' ? 'pompei' : registryTarget ?? (
        tenantsWithDedicatedServiceWorker.has(tenantSlug) ? tenantSlug : 'default'
    );

    const swUrl = resolveServiceWorkerUrl(resolvedTenant);

    if (resolvedTenant === 'default' && tenantSlug !== 'default') {
        console.info(`No dedicated service worker for tenant "${tenantSlug}", falling back to default.`);
    }

    const requestSkipWaiting = (worker) => {
        try {
            worker.postMessage("SKIP_WAITING");
        } catch (error) {
            console.warn("Unable to send SKIP_WAITING message", error);
        }
    };

    const doRegister = () => {
        navigator.serviceWorker
            .register(swUrl)
            .then((registration) => {
                if (registration.waiting) {
                    requestSkipWaiting(registration.waiting);
                }

                registration.addEventListener("updatefound", () => {
                    const newWorker = registration.installing;
                    if (!newWorker) {
                        return;
                    }

                    newWorker.addEventListener("statechange", () => {
                        if (newWorker.state === "installed" && registration.waiting) {
                            requestSkipWaiting(registration.waiting);
                        }
                    });
                });
            })
            .catch((error) => {
                console.warn("Service worker registration failed", error);
            });
    };

    if (document.readyState === "complete") {
        doRegister();
    } else {
        window.addEventListener("load", doRegister, { once: true });
    }
};

frontendMessagesPromise.then(async (frontendMessages) => {
    if (usesPublicCachePolicy && shouldRegisterServiceWorker
        && !await preparePublicServiceWorker({ expectedCache: resolvePublicStaticCache(tenantTheme) })) return null;
    return createInertiaApp({
    page: initialPage,
    title: (title) =>
        `${title} ${tenantTheme !== "Default" ? " - " + tenantTheme : ""}`,
    resolve: (name, page) => resolveTenantPageComponent(name, page),
    async setup({ el, App, props, plugin }) {
        const app = createSSRApp({ render: () => h(App, props) });
        clientApp = app;
        const frontendI18n = createFrontendI18n(props.initialPage.props?.localization, frontendMessages);
        const initialLocale = syncFrontendLocale(frontendI18n, props.initialPage.props?.localization);
        router.on('navigate', (event) => {
            const locale = syncFrontendLocale(frontendI18n, event.detail.page.props?.localization);
            setVueformLocale(app, locale);
        });

        // Clarity and Sentry follow the tenant profile (config/browser_services.php).
        startBrowserServices({
            app,
            Sentry,
            Clarity,
            router,
            initialPage: props.initialPage,
            getTenantContext,
            profile: getTenantContext().browserServices,
            production: import.meta.env.MODE === "production",
            clarityProjectId: import.meta.env.VITE_CLARITY_PROJECT_ID,
        });

        const configuredApp = app
            .use(plugin)
            .use(frontendI18n)
            .use(ZiggyVue)
            .use(pinia)
            .directive("feature", {
                mounted(el, binding) {
                    if (!isFeatureEnabled(binding.value)) {
                        el.setAttribute("disabled", true);
                        el.classList.add("opacity-50", "cursor-not-allowed");
                    }
                },
            })
            // Local component: only the maps still load visivalab-components.
            .component("VLottie", VLottie)
            .component("VMap", defineAsyncComponent(() => import('visivalab-components').then((module) => module.VMap)))
            .component("VMapLibre", defineAsyncComponent(() => import('visivalab-components').then((module) => module.VMapLibre)));

        setVueformLocale(configuredApp, initialLocale);
        if (initialPageRequiresVueform) await ensureVueformReady(configuredApp);
        // Hydrate with every icon of the server render already resolved.
        await initialIconsPromise;

        // Registered before mount(): router.init() runs inside it and the initial
        // swapComponent() re-renders the layouts. Reveal only after that commit.
        const initialNavigation = waitForInitialNavigation(router);
        const instance = configuredApp.mount(el);
        await initialNavigation;
        await nextTick();
        await window.catalogInitialRender?.ready();
        preloadLottiePlayer();
        return instance;
    },
    progress: {
        color: "#4B5563",
    },
});
}).then(() => { if (!usesPublicCachePolicy) registerTenantServiceWorker(); }).catch((error) => {
    window.catalogInitialRender?.ready();
    throw error;
});
